import React, { useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import { Grid } from "@mui/material"
import { Button, Pagination, Snackbar } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { DecisionLoadingCard } from "../components/cards/DecisionLoadingCard"
import { KonanPageHeader } from "../components/core/KonanPageHeader"
import { KonanSubHeader } from "../components/core/KonanSubHeader"
import { KonanEmptyState } from "../components/utility/KonanEmptyState"
import { ProgramCard } from "../features/Programs/components/ProgramCard"
import { KonanAPI } from "../services/KonanAPI"
import type { RootState } from "../store/ReduxStore"
import type { PaginatedDeploymentListList } from "../types/generated/api/PaginatedDeploymentListList"
import type { PaginatedProgramList } from "../types/generated/api/PaginatedProgramList"
import type { PaginatedScoreCardSetListList } from "../types/generated/api/PaginatedScoreCardSetListList"
import type { Program } from "../types/generated/api/Program"
import { useIsPermitted } from "../utils/PermissionsHelpers"

/**
 * Programs screen
 * @returns {React.ReactElement}
 */
export function Programs(): React.ReactElement {
  const { id: projectId } = useParams<{ id: string }>()

  const navigate = useNavigate()

  const { t } = useTranslation("programs")

  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canShowDetails = useIsPermitted("Retrieve program", permissions.konan, flattenedKonanPermissions)
  const canAddProgram = useIsPermitted("Add program", permissions.konan, flattenedKonanPermissions)

  const pageSize = 3

  const [openNewProgram, setOpenNewProgram] = useState<boolean>(false)
  const [duplicatedProgram, setDuplicatedProgram] = useState<Program | undefined>(undefined)

  // Pagination
  const [page, setPage] = useState<number>(0)

  //SSF States
  const [searchValue, setSearchValue] = useState<string>("")
  const [sortValue, setSortValue] = useState<SortKey>("mostRecent")
  // SSF Types And Constants
  type SortKey = keyof typeof SORT_OPTIONS
  const SORT_OPTIONS = {
    mostRecent: "-created_at",
    lastUsed: "-last_used",
  } as const

  // Fetching projects
  const { isLoading: isProjectsLoading, data: projects } = useQuery<
    AxiosResponse<PaginatedDeploymentListList>,
    AxiosError
  >(["projects"], () =>
    KonanAPI.fetchProjects({
      category: "model",
    }),
  )

  // fetch scorecardsets
  const { isLoading: isScorecardSetsLoading, data: scorecardSetsData } = useQuery<
    AxiosResponse<PaginatedScoreCardSetListList>,
    AxiosError
  >(["scorecardsets", projectId], () => KonanAPI.fetchScorecardsets({ projectId: projectId as string }), {
    enabled: !!projectId,
  })

  // check if either models or scorecardsets not empty
  const areModelsOrScorecardsetsExisting = Boolean(
    (projects?.data.results && projects?.data.results.length > 0) || scorecardSetsData?.data?.count,
  )

  // fetch programs
  const { isLoading: isProgramsLoading, data: programs } = useQuery<AxiosResponse<PaginatedProgramList>, AxiosError>(
    ["programs", projectId, page, pageSize, searchValue, sortValue],
    () => KonanAPI.fetchPrograms(projectId as string, page + 1, pageSize, searchValue, SORT_OPTIONS[sortValue]),
    {
      enabled: !!projectId && areModelsOrScorecardsetsExisting,
    },
  )

  const isBlocksLoading = isProjectsLoading || isProgramsLoading || isScorecardSetsLoading

  // enabling creating programs if these conditions are met
  const enableProgramCreation = !!projectId && !openNewProgram

  // disabling creation of programs if these conditions are met
  const shouldDisableCreation =
    !enableProgramCreation || isBlocksLoading || !areModelsOrScorecardsetsExisting || !!duplicatedProgram

  const duplicateProgram = (program: Program): void => {
    const newProgram = { ...program, name: `${program.name} - Copy` }
    setDuplicatedProgram(newProgram)
    // scroll to top
    window.scrollTo(0, 0)
  }

  return (
    <Grid container item xs={12}>
      <Grid item xs={12} textAlign={"right"} mb={2}>
        <KonanPageHeader title={t("title")} />
        <Grid mt={2}>
          <KonanSubHeader
            containerType="Programs"
            sortValue={t(sortValue)}
            setSortValue={(value) => {
              setPage(() => 0)
              setSortValue(value as SortKey)
            }}
            sortOptions={[
              { label: t("mostRecent"), value: "mostRecent" },
              { label: t("lastUsed"), value: "lastUsed" },
            ]}
            searchValue={searchValue}
            setSearchValue={(value) => {
              setPage(() => 0)
              setSearchValue(() => value as string)
            }}
            isDebouncedInput
            actions={
              canShowDetails && canAddProgram
                ? [
                    <Button
                      variant="primary"
                      startIcon={<AddCircleOutlineOutlinedIcon fontSize="small" />}
                      onClick={() => setOpenNewProgram(true)}
                      disabled={shouldDisableCreation}
                      size="regular"
                    >
                      {t("createProgramBtn")}
                    </Button>,
                  ]
                : undefined
            }
          />
        </Grid>
      </Grid>

      {!canShowDetails && (
        <Grid item flex={1} mb={1}>
          <Snackbar
            fullWidth
            severity="info"
            title={t("cannotShowDetailsSnackBar.title")}
            description={t("cannotShowDetailsSnackBar.description")}
          />
        </Grid>
      )}

      {((!isProjectsLoading && !isProgramsLoading && duplicatedProgram) || openNewProgram) && (
        <Grid item xs={12} mb={1}>
          <ProgramCard
            scorecardSetCount={scorecardSetsData?.data.count}
            isScorecardSetsLoading={isScorecardSetsLoading}
            projectsCount={projects?.data.count}
            isProjectsLoading={isProjectsLoading}
            program={duplicatedProgram}
            handleCancel={duplicatedProgram ? () => setDuplicatedProgram(undefined) : () => setOpenNewProgram(false)}
            isNewProgram={true}
            isDuplicate={true}
            canShowDetails={canShowDetails}
          />
        </Grid>
      )}

      {/* Loading state and empty states */}
      <Grid container spacing={2}>
        {isBlocksLoading ? (
          [1, 2].map((item: number) => <DecisionLoadingCard key={item} />)
        ) : programs?.data.results && programs?.data.results?.length > 0 ? (
          programs?.data?.results.map((item) => {
            return (
              <Grid item xs={12} key={item.uuid}>
                <ProgramCard
                  scorecardSetCount={scorecardSetsData?.data.count}
                  isScorecardSetsLoading={isScorecardSetsLoading}
                  projectsCount={projects?.data.count}
                  isProjectsLoading={isProjectsLoading}
                  isLoading={isBlocksLoading}
                  program={item}
                  key={item.uuid}
                  disableDuplicateButton={!!duplicatedProgram || openNewProgram}
                  duplicateProgram={duplicateProgram}
                  canShowDetails={canShowDetails}
                />
              </Grid>
            )
          })
        ) : !areModelsOrScorecardsetsExisting ? (
          <Grid item xs={12} className={"empty-container"}>
            <KonanEmptyState
              title={t("emptyState.noModelsOrscorecardsTitle")}
              buttonText={t("emptyState.createModelBtn")}
              setAction={() => navigate(`/projects/${projectId as string}/decision-engines?page=Models`)}
            />
          </Grid>
        ) : (
          enableProgramCreation && (
            <Grid item xs={12} className={"empty-container"}>
              {!searchValue ? (
                <KonanEmptyState
                  title={t("emptyState.title")}
                  subTitle={t("emptyState.subtitle")}
                  buttonText={t("emptyState.createProgrametBtn")}
                  setAction={() => setOpenNewProgram(true)}
                />
              ) : (
                // searching empty state for models
                <KonanEmptyState
                  title={t("searchEmptyState.title", {
                    value: searchValue,
                  })}
                  subTitle={t("searchEmptyState.subtitle")}
                />
              )}
            </Grid>
          )
        )}
      </Grid>

      {/* Pagination Section */}
      {!isProjectsLoading && !isProgramsLoading && programs?.data.results && programs?.data.results.length > 0 && (
        <Grid item container mt={2}>
          <Pagination
            page={page + 1}
            pageSize={pageSize}
            totalCount={programs.data.count as number}
            onPageChange={(page: number) => {
              setPage(page - 1)
              window.scrollTo(0, 0)
            }}
            variant={"complex"}
            hidePageSize={true}
          />
        </Grid>
      )}
    </Grid>
  )
}
